import type { QueryClient } from '@tanstack/react-query';
import { deviceInventoryQueryKeys } from '../-components/DeviceInventoryOperation/query';
import { getDeviceInventoryPatternGenerations } from '../-components/DeviceInventoryOperation/query/api';
import { toPatternGenerationMap, type DeviceInventoryPatternGeneration } from '../-components/DeviceInventoryOperation/query/selector';
import type { PackageType } from './type';

/**
 * loader からパターンの並べ替え（v2 先頭）を試みるべきか判定する。
 *
 * `$packageId.tsx` の loader は全パッケージ種別が通る共通経路なので、棚卸し
 * （`DeviceInventory`）以外では `fetchDeviceInventoryPatternGenerations` を
 * 一切呼ばないようにここで止める。呼んでしまうと棚卸し以外のパッケージ画面まで
 * 余計な pattern-generations API 呼び出しで遅くなる。
 */
export function shouldSortDeviceInventoryPatterns(packageType: PackageType | undefined): boolean {
  return packageType === 'DeviceInventory';
}

/**
 * 棚卸しパターンの世代一覧を取得する。
 *
 * `useDeviceInventoryPatternGenerations`（`DeviceInventoryOperation/query/index.ts`）と
 * 同じ queryKey・staleTime で `queryClient.ensureQueryData` を呼び、react-query の
 * キャッシュを共有する。素の fetch で済ませてしまうとこのキャッシュを一切温めず、
 * 同じ画面のマウントで hook 側の分と合わせて pattern-generations が二重に飛ぶため。
 *
 * 世代一覧は並べ替えのためだけに使う補助情報であり、取得に失敗してもパッケージ画面
 * 自体を落としたくない。`ensureQueryData` は失敗時に throw するので、ここで握りつぶして
 * `undefined` を返し、呼び出し元（`sortDeviceInventoryPatterns`）に並べ替えを諦めさせる。
 */
export async function fetchDeviceInventoryPatternGenerations(queryClient: QueryClient): Promise<ReadonlyMap<string, DeviceInventoryPatternGeneration> | undefined> {
  try {
    const output = await queryClient.ensureQueryData({
      queryKey: deviceInventoryQueryKeys.patternGenerations(),
      queryFn: getDeviceInventoryPatternGenerations,
      // useDeviceInventoryPatternGenerations の staleTime と揃える。同じ queryKey で
      // ここがずれると、片方だけキャッシュの有効期限判定が食い違う。
      staleTime: 5 * 60 * 1000
    });
    return toPatternGenerationMap(output);
  } catch {
    return undefined;
  }
}